import { Injectable, EventEmitter, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AppConfig } from '../model/config';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { List } from 'immutable';
import { HttpHelper } from './http.service';
import { Roles } from '../common/enums';


@Injectable()
export class AuthService implements OnDestroy {
  public authReady$ = new EventEmitter<boolean>();
  public groupsReady$ = new EventEmitter<boolean>();

  private readonly _destroying$ = new Subject<void>();
  private userGroups: List<string>;

  public constructor(
    private readonly authService: MsalService,
    private readonly configuration: AppConfig,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly http: HttpHelper) {

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => { return status === InteractionStatus.None }),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      })

  }

  public ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  public logout() {
    this.authService.logout();
  }

  public getUser() {
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length > 0)
      return accounts[0] ? decodeURIComponent(accounts[0].name) : "";
  }

  public haveAnyAccessToPlanner(): boolean {
    return this.isInAnyRole([Roles.Planner]);
  }

  public isInAnyRole(roles: string[]): boolean {
    let isInAnyRole = false;

    roles.forEach(r => {
      if (this.isInRole(r)) {
        isInAnyRole = true;
      }
    });

    return isInAnyRole;
  }

  public isInRole(role: string): boolean {

    if (!this.userGroups) throw new Error("UserGroup not loaded");

    return this.userGroups.includes(role);
  }

  public acquireToken() {
    const accessTokenRequest = {
      scopes: [this.configuration.apiScope]
    }
    return this.authService.acquireTokenSilent(accessTokenRequest);
  }

  private async checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      activeAccount = this.authService.instance.getActiveAccount();
    }

    if (activeAccount) {
      this.acquireToken().subscribe({
        next: data => {
          this.authReady$.next(true);
        }, error: err => {
          this.relogin();
        }
      });
    }
  }

  public async initUserGroups() {

    if (this.userGroups) {
      return;
    }

    await this.loadGroups().then((result: List<string>) => {
      this.userGroups = result;
      this.groupsReady$.next(true);
    });
  }

  private async loadGroups(): Promise<List<string>> {
    const uri = this.configuration.backendUrl;

    return await this.http.get(uri + "/api/UserInfo/groups/all", (result: List<string>) => {
      this.userGroups = result;
    }, (error) => { }).toPromise<List<string>>();
  }

  private relogin() {
    const loginRequest = {
      scopes: [this.configuration.apiScope]
    };

    this.authService.loginRedirect(loginRequest);
  }
}
